/**
 * SSE (Server-Sent Events) event type constants
 * Matches backend EventType enum from ai_agent/domain/value_objects/chat/event_type.py
 *
 * @module common/constants/sse-event
 */

/**
 * SSE event types sent from backend
 *
 * @remarks
 * These constants match exactly with the backend EventType enum to ensure
 * proper event parsing and handling in the SSE stream.
 *
 * @see {@link src/ai_agent/domain/value_objects/chat/event_type.py}
 *
 * @example
 * ```typescript
 * import { SSE_EVENT_TYPE } from '~/common/constants';
 *
 * if (event.event === SSE_EVENT_TYPE.MESSAGE) {
 *   // Handle message event
 * }
 * ```
 */
export const SSE_EVENT_TYPE = {
  /**
   * Chat session information event
   * Sent at the beginning of the stream with session metadata
   */
  CHAT_SESSION: "chat_session",

  /**
   * Processing step event
   * Indicates current processing step (e.g., thinking, retrieving, generating)
   */
  STEP: "step",

  /**
   * Message chunk event
   * Contains incremental content chunks for streaming response
   */
  MESSAGE: "message",

  /**
   * Follow-up queries event
   * Provides suggested questions for the user to continue the conversation
   */
  FOLLOW_UP_QUERIES: "follow_up_queries",

  /**
   * End of stream event
   * Signals completion of the SSE stream
   */
  END: "end",
} as const;

/**
 * Processing step types
 * Matches backend StepType enum from ai_agent/domain/value_objects/chat/step_type.py
 *
 * @remarks
 * These represent the different stages of AI processing that can be
 * displayed to the user during response generation.
 *
 * @see {@link src/ai_agent/domain/value_objects/chat/step_type.py}
 */
export const PROCESSING_STEP_TYPE = {
  /**
   * AI is analyzing and thinking about the query
   */
  THINKING: "Thinking…",

  /**
   * AI is searching and retrieving relevant context from knowledge base
   */
  RETRIEVING_CONTEXT: "Retrieving relevant context…",

  /**
   * AI is generating the final answer
   */
  GENERATING_ANSWER: "Generating answer…",
} as const;

/**
 * Stream status types for message state management
 *
 * @remarks
 * These statuses track the lifecycle of a message or SSE connection
 * and are used to display appropriate UI states.
 */
export const STREAM_STATUS = {
  /**
   * Message is stored locally (not yet sent to server)
   */
  LOCAL: "local",

  /**
   * Initial loading state when waiting for first response
   */
  LOADING: "loading",

  /**
   * Stream is actively receiving and updating content
   */
  UPDATING: "updating",

  /**
   * Stream completed successfully
   */
  SUCCESS: "success",

  /**
   * Stream encountered an error
   */
  ERROR: "error",

  /**
   * Stream was aborted by user or system
   */
  ABORT: "abort",
} as const;

/**
 * SSE connection states
 *
 * @remarks
 * Tracks the state of the EventSource connection for reconnection logic
 * and error handling.
 */
export const SSE_CONNECTION_STATE = {
  /**
   * Connection is not established
   */
  DISCONNECTED: "disconnected",

  /**
   * Attempting to establish connection
   */
  CONNECTING: "connecting",

  /**
   * Connection is active and streaming
   */
  CONNECTED: "connected",

  /**
   * Connection failed due to error
   */
  ERROR: "error",

  /**
   * Connection was closed intentionally
   */
  CLOSED: "closed",
} as const;
